import React, { useState, useEffect } from 'react';
import useFetch from '../../hooks/useFetch';
import {
  Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField
} from "@mui/material";

interface MakeCheckPaymentDialogProps {
  open: boolean;
  storeId: number;
  onClose: () => void;
  onPaymentSuccess: (newTransaction: any) => void;
}

const MakeCheckPaymentDialog: React.FC<MakeCheckPaymentDialogProps> = ({ open, storeId, onClose, onPaymentSuccess }) => {
  const [amount, setAmount] = useState('');
  const [validationError, setValidationError] = useState('');
  const [showApiError, setShowApiError] = useState(false);

  const { fetchData: makePayment, isLoading, errorContent, error } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/payments/make-check-payment`,
    method: 'POST',
  });

  useEffect(() => {
    if (open) {
      setAmount('');
      setValidationError('');
      setShowApiError(false);
    }
  }, [open]);

  useEffect(() => {
    if (validationError) {
      const timer = setTimeout(() => setValidationError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [validationError]);

  useEffect(() => {
    if (showApiError || errorContent) {
      const timer = setTimeout(() => {
        setShowApiError(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showApiError, errorContent]);

  const handleMakePayment = async () => {
    setValidationError('');
    
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setValidationError("Please enter a valid amount greater than 0.");
      return;
    }

    const result = await makePayment({
      store_id: storeId,
      amount: Number(amount)
    });

    if (result?.success) {
      onPaymentSuccess(result.data);
      onClose();
    } else {
      setShowApiError(true);
      if (!errorContent && result?.message) {
        setValidationError(result.message.replace(/^Error in \w+:\s*/i, ""));
      }
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => !isLoading && onClose()} 
      maxWidth="xs" 
      fullWidth 
      disableScrollLock
      PaperProps={{ className: 'payouts-dialog-paper' }}
    >
      <DialogTitle className="payouts-dialog-title">
        Make Check Payment
      </DialogTitle>
      <DialogContent className="payouts-dialog-content">
        <TextField
          autoFocus 
          fullWidth
          label="Amount (USD)"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !isLoading) handleMakePayment(); }}
          variant="outlined"
          size="small"
          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          className="payouts-dialog-input"
          disabled={isLoading}
        />
        <div className="payouts-dialog-error-container">
          {(validationError || (showApiError && error)) && (
            <div className="payouts-dialog-error-text">
              {validationError ? validationError : (
                error?.code === 401
                  ? errorContent
                  : (error?.message?.toLowerCase().includes('failed payment state'))
                    ? "Unable to make payment at the moment. Please try again later."
                    : error?.message?.replace(/^Error in \w+:\s*/i, "")
              )}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions className="payouts-dialog-actions payouts-dialog-actions-container">
        <div className="payouts-dialog-btn-group">
          <Button 
            onClick={onClose} 
            size="small"
            disabled={isLoading}
            className="payouts-dialog-cancel-btn"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleMakePayment} 
            size="small" 
            variant="contained" 
            disableElevation
            disabled={isLoading}
            className="payouts-dialog-submit-btn"
          >
            {isLoading ? 'Processing...' : 'Submit'}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default MakeCheckPaymentDialog;
